import {
  Controller,
  useWatch,
  type Control,
  type FieldPath,
  type FieldValues
} from 'react-hook-form';
import { useFieldErrorMessage } from './useFieldErrorMessage';
import { FormFieldSelect, type FormFieldSelectProps } from '@/components/form/FormFieldSelect';

export interface FormSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<FormFieldSelectProps, 'name' | 'value' | 'onValueChange' | 'error'> {
  /**
   * React Hook Form control object
   */
  control: Control<TFieldValues>;

  /**
   * Field name in the form
   */
  name: TName;

  /**
   * Default value for the field
   */
  defaultValue?: string;

  /**
   * Runs after the form-state update — for follow-up effects on OTHER fields
   * (e.g. dropping the new mainRole from the selected secondaryRoles).
   */
  onValueChange?: (value: string | undefined) => void;
}

/**
 * React Hook Form adapter for FormFieldSelect
 * Automatically handles form state, validation, and errors
 *
 * @example
 * ```tsx
 * const { control } = useForm<FormData>();
 *
 * <FormSelect
 *   control={control}
 *   name="status"
 *   label="Status"
 *   options={[
 *     { value: 'active', label: 'Active' },
 *     { value: 'inactive', label: 'Inactive' }
 *   ]}
 *   required
 * />
 * ```
 */
export function FormSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  defaultValue,
  onValueChange,
  ...fieldProps
}: FormSelectProps<TFieldValues, TName>) {
  const fieldErrorMessage = useFieldErrorMessage();

  // Display from the raw watched value, not Controller's field.value, which
  // masks a cleared (undefined) value back to the field's original value. See
  // FormUserSelect for the full explanation.
  const value = useWatch({ control, name });

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue as never}
      render={({ field, fieldState }) => (
        <FormFieldSelect
          {...field}
          {...fieldProps}
          // Override the spread field.value (masked on clear) with the unmasked
          // watched value — last prop wins.
          value={value}
          onValueChange={(next) => {
            field.onChange(next);
            onValueChange?.(next);
          }}
          error={fieldErrorMessage(fieldState.error)}
        />
      )}
    />
  );
}

FormSelect.displayName = 'FormSelect';
